<template>
    <div aria-labelledby="modal-title" aria-modal="true" class="fixed z-10 inset-0 overflow-y-auto" role="dialog">
        <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div aria-hidden="true" class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <span aria-hidden="true" class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle z-100">
                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div class="sm:flex sm:items-start" style="max-height: 350px; overflow-y: auto">
                        <div class="mt-3 text-center sm:mt-0 w-full sm:text-left">
                            <h3 id="modal-title" class="text-lg leading-6 font-medium text-gray-900">
                                Enviar Anexo
                            </h3>
                            <div class="mt-2">
                                <FontAwesomeIcon :icon="(getFontAwesomeIconFromMIME(resolvedFile?.type ?? '') as any)" class="w-full"/>
                                <p class="text-sm text-gray-500 w-full text-center">
                                    {{ resolvedFile?.name }} - {{ sizeOf(resolvedFile?.size ?? 0) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bg-gray-50 px-4 py-3 sm:px-6 buttons-area">
                    <button
                        :disabled="sending"
                        class="disabled:opacity-25 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ml-3 w-auto text-sm"
                        type="button"
                        @click="sendFile">
                        Enviar Anexo
                    </button>
                    <button
                        class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mt-0 ml-3 w-auto text-sm"
                        type="button"
                        @click="$emit('closeModal')">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from "vue"
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {
    faFile,
    faFileArchive,
    faFileCode,
    faFileExcel,
    faFilePdf,
    faFilePowerpoint,
    faFileText,
    faFileWord
} from "@fortawesome/free-solid-svg-icons"
import {useMessageStore} from "@src/stores/messages";

const emit = defineEmits(['closeModal'])

const props = defineProps<{
    file: DataTransferItem | File
}>()

const resolvedFile = ref<File | null>(null)
const sending = ref(false)

const ALLOWED_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'odt',
    'xls', 'xlsx', 'ods',
    'ppt', 'pptx', 'odp',
    'txt', 'html', 'json',
    'gz', 'zip'
]

onMounted(() => {
    resolvedFile.value = props.file instanceof File
        ? props.file
        : props.file.getAsFile()
})

const sendFile = async () => {
    if (!resolvedFile.value || sending.value) return

    const messageStore = useMessageStore()

    sending.value = true
    emit('closeModal')

    await messageStore.submitMedia(
        [resolvedFile.value],
        'document',
        ALLOWED_EXTENSIONS
    )

    sending.value = false
}

function getFontAwesomeIconFromMIME(mimeType: string) {
    const iconMap: Record<string, unknown> = {
        "application/pdf": faFilePdf,
        "application/msword": faFileWord,
        "application/vnd.ms-word": faFileWord,
        "application/vnd.oasis.opendocument.text": faFileWord,
        "application/vnd.openxmlformats-officedocument.wordprocessingml": faFileWord,
        "application/vnd.ms-excel": faFileExcel,
        "application/vnd.openxmlformats-officedocument.spreadsheetml": faFileExcel,
        "application/vnd.oasis.opendocument.spreadsheet": faFileExcel,
        "application/vnd.ms-powerpoint": faFilePowerpoint,
        "application/vnd.openxmlformats-officedocument.presentationml": faFilePowerpoint,
        "application/vnd.oasis.opendocument.presentation": faFilePowerpoint,
        "text/plain": faFileText,
        "text/html": faFileCode,
        "application/json": faFileCode,
        "application/gzip": faFileArchive,
        "application/zip": faFileArchive,
    }

    for (const key in iconMap) {
        if (mimeType.startsWith(key)) return iconMap[key]
    }

    return faFile
}

const sizeOf = (bytes: number): string => {
    if (bytes === 0) return "0.00 B"
    const e = Math.floor(Math.log(bytes) / Math.log(1024))
    return (bytes / Math.pow(1024, e)).toFixed(2) + ' ' + ' KMGTP'.charAt(e) + 'B'
}
</script>

<style scoped>
svg {
    height: 100px;
    margin: 20px 0;
}
.buttons-area {
    display: flex; flex-direction: row-reverse; flex-wrap: nowrap; justify-content: flex-start; align-content: stretch; align-items: center;
}
.buttons-area button {
    cursor: pointer;
    padding: 8px 16px;
}
</style>